/* UTILS */
import { defineStore } from 'pinia';

import { getCompany } from '@/api/calls/company/company.api';
/* API IMPORTS */
import { getMe } from '@/api/calls/me/me.api';
/* ENUMS */
import { permissionsIdToName } from '@/enums/permissions';
import { cloneDeep } from '@/helpers/generic';
/* TYPES */
import type { Auth, AuthHooks, AuthUser, Me, MeManagers } from '@/types/api/me/me';
import type { Company } from '@/types/company/company.types';

import { useGlobalStore } from './global';

/**
 * This function will map new permissions values into the old and readable one
 */
function getFullPermissions(data: Auth) {
  // replace shortened permission ID to usable name
  const oldPermissions = cloneDeep(data.permissions);

  const newPermissions = oldPermissions.map((p): string => {
    // if not found, will use the value given by the server
    return permissionsIdToName[p] || p;
  });

  data.permissions = newPermissions;

  return data;
  // end of shortened permission
}
/**
 * This store contains all data about the current connected user
 */
export const useAuthStore = defineStore('auth', {
  state: (): Me & AuthHooks => {
    return {
      company_uuid: null,
      customer_uuid: '',
      has_manager_accounts: false,
      managers: [] as MeManagers[],
      permissions: [],
      sharepools: [],
      is_2FA_enabled: false,
      is_2FA_required: false,
      is_pole_emploi: false,
      is_recruiter_pe: false,
      user: {} as AuthUser,
      company: {} as Company,
      isLoginDone: false,
      isFirstConnection: false,
      isLoadMeDone: false
    };
  },
  getters: {
    /**
     * Get the current locale ID to fetch right translation files
     *
     * @returns {User['locale_id']} The locale ID
     */
    getLocaleId(state) {
      return state.user?.locale_id;
    },

    /**
     * Get all data about user
     *
     * @returns {User} User's data
     */
    getFullUser(state): AuthUser {
      return state.user;
    },
    /**
     * Get customer UUID
     *
     * @returns {String} customer UUID
     */
    getCustomerUUID(state) {
      return state.customer_uuid;
    },
    /**
     * Is the current connected user a france travail recruiter
     * @returns {boolean} is the current user a france travail recruiter
     */
    getIsFranceTravailRecruiter(state) {
      return state.is_recruiter_pe;
    },
    /**
     * Allows to check if the current user has a given permission
     * @param {String} permission - name of permission to check
     *
     * @returns {Boolean} has the current user the given permission
     */
    hasPermission: state => (permission: string) => {
      return state.permissions?.includes(permission);
    },
    /**
     * Allows to check if the current user has all requested permissions
     * @param {String[]} permissionsList - array of permissions to check
     *
     * @returns {Boolean} true if access granted
     */
    hasPermissionsList: state => (permissionsList: string[]) => {
      // build a new array with granted permissions
      const authorizedPermissions = permissionsList.filter(permission => {
        return state.permissions?.includes(permission) === true;
      });

      // can access if granted permissions are the same than the requested ones
      return authorizedPermissions.length === permissionsList.length;
    },
    /**
     * Get data about user's company
     *
     * @returns {Company} User's data
     */
    getCompany(state) {
      return state.company;
    },
    /**
     *
     * @param state
     * @returns
     */
    getMultiCompany: state => {
      return Array.isArray(state.managers) && state.managers.length > 1;
    },
    /**
     *
     * @returns
     */
    isManagerSingleCompany() {
      return !this.getMultiCompany() && !this.hasPermission('company_admin_r') && this.hasPermission('manager_r');
    },
    /**
     * Get data about user's sharepools (only uuids)
     *
     * @returns {String[]} List of user's sharepools
     */
    getSharepools(state) {
      return state.sharepools.map(sharepoolUuid => sharepoolUuid.toLowerCase());
    },
    /**
     * Allows to know if a recruiter is in more than one sharepool
     *
     * @returns {Boolean} true if more than 1
     */
    hasMultipleSharepools(state) {
      return state.sharepools.length > 1;
    },
    /**
     * Get data about recruiter's sharepool, if multiple sharepools, return undefined
     *
     * @returns {String | undefined} Sharepool UUID
     */
    getSharepoolSpace(state) {
      return state.sharepools.length > 1 ? undefined : state.sharepools[0]?.toLowerCase();
    },


    /* HOMEMADE HOOKS */
    /**
     * Get if the login is done (used as "hook")
     */
    getIsLoginDone(state) {
      return state.isLoginDone;
    },
    getCompanyUuid(state) {
      return state.company_uuid;
    },
    getPathToSharepoolOrCompany() {
      if (this.getSharepoolSpace) {
        return `sharepool/${this.getSharepoolSpace}`;
      }
      return `company/${this.getCompanyUuid}`;
    },
    getSharepoolOrCompanyParams(): Record<string, string> {
      if (this.getSharepoolSpace) {
        return { sharepool_uuid: this.getSharepoolSpace };
      }
      return { company_uuid: this.getCompanyUuid };
    },
    getIsFirstConnection(state) {
      return state.isFirstConnection;
    }
  },
  actions: {
    /**
     * Set the hook isLoginDone to true
     */
    setLoginDone() {
      this.isLoginDone = true;
    },
    setIsFirstConnection() {
      this.isFirstConnection = true;
    },
    /**
     * Update the extra data (user_data) inside user instance
     */
    setUserExtraData(newUserData) {
      this.user.user_data = { ...this.user.user_data, ...newUserData };
    },
    /**
     * Fetch the data about the current connected user
     */
    loadMe() {
      return new Promise<void>((resolve, reject) => {
        getMe()
          .then(async response => {
            this.$patch({
              ...getFullPermissions(response)
            });

            // Redirect to terms of use if needed
            if (this.hasPermission('cst_na')) {
              return window.location.assign(`${useGlobalStore().auth_url}/terms-of-use`);
            }

            // Redirect to MFA if needed
            if (this.hasPermission('mfa_req')) {
              return window.location.assign(`${useGlobalStore().auth_url}/mfa`);
            }
            if (this.$state.is_partner) {
              return window.location.assign(useGlobalStore().auth_url);
            }

            if (this.hasPermission('recruiter_r')) {
              await this.loadCompany();

              resolve();
            } else {
              resolve();
            }
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    /**
     * Refetch the data about the current connected user
     */
    async reloadMe() {
      const response = await getMe();

      this.$patch({
        ...getFullPermissions(response)
      });
    },
    loadCompany() {
      return new Promise<void>(resolve => {
        getCompany(this.company_uuid).then(companyData => {
          this.$patch({
            company: companyData
          });

          resolve();
        });
      });
    }
  }
});
